import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_URL, authFetch, parseJsonResponse, responseErrorMessage } from '../utils/auth';
import ToolSidebar from '../utils/ToolSideBar';
import '../../css/client/ClientConversationList.css';

function titleCaseName(value) {
  return (value || '').trim().replace(/[A-Za-z]+/g, (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
}

function displayName(contact) {
  const firstName = titleCaseName(contact.first_name);
  const lastName = titleCaseName(contact.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return contact.short_name || contact.name || fullName || contact.full_name || contact.email || 'Contact';
}

function roleLabel(contact) {
  return contact.role_label || contact.role || 'Portal contact';
}

function initialsFor(contact) {
  const label = displayName(contact);
  return label.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'C';
}

export default function ContactsPage({ partner }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [messageDraft, setMessageDraft] = useState({ recipient: null, subject: '', message: '' });
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    async function loadContacts() {
      setLoading(true);
      setError('');
      try {
        const response = await authFetch(API_URL + '/contacts/');
        const data = await parseJsonResponse(response);
        if (!response.ok) throw new Error(responseErrorMessage(response, data, 'Unable to load contacts.'));
        setContacts(Array.isArray(data?.results) ? data.results : []);
      } catch (err) {
        setError(err.message || 'Unable to load contacts right now.');
        setContacts([]);
      } finally {
        setLoading(false);
      }
    }
    loadContacts();
  }, []);

  const filteredContacts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return contacts.filter((contact) => {
      const name = displayName(contact);
      const email = contact.email || '';
      const contactRole = roleLabel(contact);
      const matchesSearch = !needle || name.toLowerCase().includes(needle) || email.toLowerCase().includes(needle) || contactRole.toLowerCase().includes(needle);
      const matchesRole = !role || roleLabel(contact) === role;
      return matchesSearch && matchesRole;
    });
  }, [contacts, search, role]);

  const roles = partner ? ['Client', 'HR'] : Array.from(new Set(contacts.map((contact) => roleLabel(contact)))).filter(Boolean);

  async function startConversation(e) {
    e.preventDefault();
    if (!messageDraft.recipient || !messageDraft.message.trim()) {
      setError('Select a contact and write a message first.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const response = await authFetch(API_URL + '/conversations/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: messageDraft.recipient.id,
          subject: messageDraft.subject,
          message: messageDraft.message,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(responseErrorMessage(response, data, 'Unable to start conversation.'));
      const conversationId = data?.conversation?.id || data?.id;
      if (!conversationId) throw new Error('Conversation was created without a conversation id.');
      navigate('/messages/' + conversationId);
    } catch (err) {
      setError(err.message || 'Unable to start conversation.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className='staff-shell'>
      <ToolSidebar active='contacts' isPartner={partner} />
      <main className='staff-page conversation-workspace'>
        <header className='staff-header'>
          <span>Directory</span>
          <h1>Contacts</h1>
          <p>Only people you are allowed to message are shown here.</p>
        </header>

        <section className='staff-card conversation-toolbar'>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Search contacts' />
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value=''>{partner ? 'All' : 'All roles'}</option>
            {roles.map((item) => <option key={item} value={item}>{partner && item === 'Client' ? 'Clients' : item}</option>)}
          </select>
        </section>

        {error && <p className='conversation-error'>{error}</p>}

        {loading ? (
          <section className='staff-card'><p className='staff-empty'>Loading contacts...</p></section>
        ) : filteredContacts.length === 0 ? (
          <section className='empty-state'><h3>No contacts available</h3><p>Your allowed contacts will appear here when relationships are available.</p></section>
        ) : (
          <section className='contact-grid'>
            {filteredContacts.map((contact) => (
              <article className='contact-card identity-card' key={contact.id}>
                <div className='identity-main'>
                  <div className='identity-avatar'>{initialsFor(contact)}</div>
                  <div className='identity-copy'>
                    <div className='identity-heading'>
                      <strong>{displayName(contact)}</strong>
                      <span className='role-badge'>{roleLabel(contact)}</span>
                    </div>
                    <small>{contact.email || 'No email on file'}</small>
                    {contact.context && <small>{contact.context}</small>}
                  </div>
                </div>
                <button type='button' onClick={() => setMessageDraft({ recipient: contact, subject: '', message: '' })}>Message</button>
              </article>
            ))}
          </section>
        )}

        {messageDraft.recipient && (
          <section className='staff-card new-message-panel'>
            <div className='staff-card-header message-compose-header'>
              <div className='identity-main'>
                <div className='identity-avatar'>{initialsFor(messageDraft.recipient)}</div>
                <div className='identity-copy'>
                  <h2>Message {displayName(messageDraft.recipient)}</h2>
                  <span className='role-badge'>{roleLabel(messageDraft.recipient)}</span>
                  <small>{messageDraft.recipient.email}</small>
                </div>
              </div>
              <button type='button' className='btn btn-secondary' onClick={() => setMessageDraft({ recipient: null, subject: '', message: '' })}>Close</button>
            </div>
            <form onSubmit={startConversation} className='message-form compact-message-form'>
              <input value={messageDraft.subject} onChange={(e) => setMessageDraft((prev) => ({ ...prev, subject: e.target.value }))} placeholder='Subject' maxLength={180} />
              <textarea value={messageDraft.message} onChange={(e) => setMessageDraft((prev) => ({ ...prev, message: e.target.value }))} placeholder='Write the first message' maxLength={5000} />
              <div className='composer-footer'>
                <small>{messageDraft.message.length}/5000</small>
                <button type='submit' disabled={sending}>{sending ? 'Sending...' : 'Send Message'}</button>
              </div>
            </form>
          </section>
        )}
      </main>
    </div>
  );
}